import { useState, useRef } from "react";
import { ImagePlus, Loader2 } from "lucide-react";
import { isHeicFile, convertHeicToJpeg } from "@/lib/utils/heic-converter";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";

interface ImageUploadWithHomescreenProps {
  onImageSelect: (file: File, sendToHomescreen: boolean) => void;
}

export function ImageUploadWithHomescreen({
  onImageSelect,
}: ImageUploadWithHomescreenProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [sendToHomescreen, setSendToHomescreen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isConverting, setIsConverting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const isImage = file.type.startsWith("image/") || isHeicFile(file);
    if (!isImage) return;

    setIsOpen(true);

    if (isHeicFile(file)) {
      setIsConverting(true);
      try {
        const convertedFile = await convertHeicToJpeg(file);
        setSelectedFile(convertedFile);
        setPreviewUrl(URL.createObjectURL(convertedFile));
      } catch (error) {
        console.error("Failed to convert HEIC file:", error);
        setSelectedFile(file);
        setPreviewUrl(URL.createObjectURL(file));
      } finally {
        setIsConverting(false);
      }
    } else {
      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const handleSend = () => {
    if (selectedFile) {
      onImageSelect(selectedFile, sendToHomescreen);
      handleClose();
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    setSelectedFile(null);
    setPreviewUrl(null);
    setSendToHomescreen(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,.heic,.heif"
        className="hidden"
        onChange={handleFileChange}
        id="image-upload-input"
      />
      <label htmlFor="image-upload-input" className="cursor-pointer">
        <ImagePlus className="text-muted-foreground" />
      </label>

      <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Send Image</DialogTitle>
            <DialogDescription>
              Preview your image and choose where to send it
            </DialogDescription>
          </DialogHeader>

          {isConverting ? (
            <div className="flex flex-col items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
              <span className="mt-2 text-sm text-gray-500">Converting image...</span>
            </div>
          ) : previewUrl ? (
            <div className="flex justify-center py-4">
              <img
                src={previewUrl}
                alt="Preview"
                className="max-h-64 rounded-lg object-contain"
              />
            </div>
          ) : null}

          <div className="flex items-center space-x-2 rounded-md border p-4">
            <Checkbox
              id="homescreen"
              checked={sendToHomescreen}
              onCheckedChange={(checked) =>
                setSendToHomescreen(checked === true)
              }
            />
            <Label
              htmlFor="homescreen"
              className="cursor-pointer text-sm font-normal leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              Send to teen&apos;s home screen
            </Label>
          </div>

          <DialogFooter className="sm:justify-between">
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="button" onClick={handleSend} disabled={isConverting || !selectedFile}>
              Send Image
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
